/* eslint-disable camelcase */
import {
  BASE_FLOW_BUILDER_API_URL,
  MessageMediaTypes,
} from 'components/flowBuilder/constants';
import {
  FILE_UPLOAD_URL,
  IMAGE_UPLOAD_URL,
} from 'components/flowBuilder/constants/apiRoutes';
import {
  LegacyTagsDict,
  LEGACY_TAG_REGEX,
} from 'components/flowBuilder/constants/tags';
import {
  DraftFlow,
  Flow,
  MessageTemplateContexts,
  PreviewRequestParams,
  TriggerEvent,
} from 'components/flowBuilder/types';
import { Action } from 'components/flowBuilder/types/actions';
import {
  isRichMessageAction,
  isSendMessageAction,
} from 'components/flowBuilder/types/actions/typeGuards';
import { MessagePreview } from 'components/flowBuilder/types/messages';
import { isMessagePreview } from 'components/flowBuilder/types/messages/typeGuards';
import { FlowContext } from 'components/flowBuilder/types/tags';
import { isFlowContext } from 'components/flowBuilder/types/tags/typeGuards';
import Helpers from 'controllers/helpers';
import { api } from 'controllers/network/apiClient';

export interface SaveImageFileResponse {
  media_url: string;
  success?: boolean;
}

const isSaveImageFileResponse = (
  response: unknown,
): response is SaveImageFileResponse => {
  const responseValue = response as SaveImageFileResponse;

  if (!responseValue || typeof responseValue !== 'object') return false;
  if (!responseValue.media_url || typeof responseValue.media_url !== 'string')
    return false;

  // IMAGE_UPLOAD_URL response returns a 'success' boolean, FILE_UPLOAD_URL does not
  if (
    responseValue.success !== undefined &&
    typeof responseValue.success !== 'boolean'
  )
    return false;

  return true;
};

const constructFormData = (
  file: File,
  endpoint: string,
  mediaType: 'image' | 'video' = 'image',
) => {
  const formData = new FormData();

  if (endpoint === FILE_UPLOAD_URL) {
    formData.append('file', file);
    formData.append('targetType', MessageMediaTypes.MMS);
  } else {
    // For legacy endpoint, use 'image' or 'video' as the field name
    formData.append(mediaType, file);
  }

  return formData;
};

export const saveImageFile = async (
  file: File,
  endpoint: string = IMAGE_UPLOAD_URL,
): Promise<string> => {
  const formData = constructFormData(file, endpoint);

  const response: unknown = await api.postFormData(endpoint, formData);

  if (!isSaveImageFileResponse(response))
    throw new Error('Unexpected save image file response');

  if (response.success === false)
    throw new Error('Unsuccessful image file upload');

  return response.media_url;
};

export const saveImageFromUrl = async (
  url: string,
  rcsMessageCategory?: string,
): Promise<string> => {
  const formData = new FormData();
  formData.append('url', url);

  // Use appropriate targetType based on context
  const targetType =
    rcsMessageCategory === 'CAROUSEL'
      ? MessageMediaTypes.RCS_CAROUSEL
      : rcsMessageCategory === 'CARD'
      ? MessageMediaTypes.RCS_CARD
      : MessageMediaTypes.MMS;
  formData.append('targetType', targetType);

  const response: unknown = await api.postFormData(FILE_UPLOAD_URL, formData);

  if (!isSaveImageFileResponse(response))
    throw new Error('Unexpected save image file response');

  return response.media_url;
};

export const saveVideoFromUrl = async (
  url: string,
  rcsMessageCategory?: string,
): Promise<string> => {
  const formData = new FormData();
  formData.append('url', url);

  const targetType =
    rcsMessageCategory === 'CAROUSEL'
      ? MessageMediaTypes.RCS_CAROUSEL
      : MessageMediaTypes.RCS_CARD;
  formData.append('targetType', targetType);

  const response: unknown = await api.postFormData(FILE_UPLOAD_URL, formData);

  if (!isSaveImageFileResponse(response))
    throw new Error('Unexpected save video file response');

  return response.media_url;
};

export const saveVideoFile = async (
  file: File,
  endpoint: string = FILE_UPLOAD_URL, // Default to v2 endpoint for videos
  rcsMessageCategory?: string,
): Promise<string> => {
  const formData = new FormData();

  // v2/files/upload endpoint expects 'file' field and 'targetType'
  if (endpoint === FILE_UPLOAD_URL) {
    formData.append('file', file);
    const targetType =
      rcsMessageCategory === 'CAROUSEL'
        ? MessageMediaTypes.RCS_CAROUSEL
        : MessageMediaTypes.RCS_CARD;
    formData.append('targetType', targetType);
  } else {
    // Fallback for other endpoints (though videos should use v2)
    formData.append('video', file);
  }

  const response: unknown = await api.postFormData(endpoint, formData);

  if (!isSaveImageFileResponse(response))
    throw new Error('Unexpected save video file response');

  // v2 endpoint doesn't return 'success' field, just media_url
  // Legacy endpoint returns success field
  if (response.success === false)
    throw new Error('Unsuccessful video file upload');

  return response.media_url;
};

interface Kwargs {
  key: string;
  type: string;
}

interface ContextAttributes {
  key: string;
  title: string;
  kwargs?: Array<Kwargs>;
}

const isContextAttributes = (
  attributes: unknown,
): attributes is ContextAttributes => {
  const attributesValue = attributes as ContextAttributes;

  if (!attributesValue || typeof attributesValue !== 'object') return false;

  return true;
};

interface ContextSchema {
  title: string;
  description: string;
  attributes: Array<ContextAttributes>;
}

const isContextSchema = (schema: unknown): schema is ContextSchema => {
  const schemaValue = schema as ContextSchema;

  if (!schemaValue || typeof schemaValue !== 'object') return false;
  if (!schemaValue.title || typeof schemaValue.title !== 'string') return false;
  if (!schemaValue.description || typeof schemaValue.description !== 'string')
    return false;
  if (!schemaValue.attributes || !isContextAttributes(schemaValue.attributes))
    return false;

  return true;
};

export interface Context {
  key: string;
  schema: ContextSchema;
}

const isContext = (context: unknown): context is Context => {
  const contextValue = context as Context;

  if (!contextValue || typeof contextValue !== 'object') return false;
  if (!contextValue.key || typeof contextValue.key !== 'string') return false;
  if (!contextValue.schema || !isContextSchema(contextValue.schema))
    return false;

  return true;
};

interface ContextsResponse {
  contexts: Context[];
}

const isContextsResponse = (
  response: unknown,
): response is ContextsResponse => {
  const responseValue = response as ContextsResponse;

  if (!responseValue || typeof responseValue !== 'object') return false;

  if (!responseValue.contexts || !Array.isArray(responseValue.contexts))
    return false;
  if (responseValue.contexts.some((context) => !isContext(context)))
    return false;

  return true;
};

export const fetchContexts = async (): Promise<Context[]> => {
  const response: unknown = await api.get(
    `${BASE_FLOW_BUILDER_API_URL}contexts/`,
  );
  if (!isContextsResponse(response))
    throw new Error('Unexpected contexts response');
  return response.contexts;
};

interface FlowContextsResponse {
  contexts: FlowContext[];
}

const isFlowContextsResponse = (
  response: unknown,
): response is FlowContextsResponse => {
  const responseValue = response as FlowContextsResponse;

  if (!responseValue || typeof responseValue !== 'object') return false;
  if (!responseValue.contexts || !Array.isArray(responseValue.contexts))
    return false;
  if (responseValue.contexts.some((context) => !isFlowContext(context)))
    return false;

  return true;
};

export const fetchFlowContexts = async (
  events: TriggerEvent[],
): Promise<FlowContext[]> => {
  const eventsParam = events.map((event) => `"${event.eventType}"`).join();
  const response: unknown = await api.get(
    `${BASE_FLOW_BUILDER_API_URL}contexts/tags/func?events=[${eventsParam}]`,
  );
  if (!isFlowContextsResponse(response))
    throw new Error('Unexpected contexts response');
  return response.contexts;
};

interface MessagePreviewPayload {
  contexts?: MessageTemplateContexts;
  params: {
    content: string;
  };
}

export const fetchMessagePreview = async (
  params: PreviewRequestParams,
  contexts?: MessageTemplateContexts,
): Promise<MessagePreview> => {
  const payload: MessagePreviewPayload = {
    params,
  };

  if (contexts) payload.contexts = contexts;

  const response: unknown = await api.post(
    `${BASE_FLOW_BUILDER_API_URL}actions/preview/`,
    payload,
  );

  if (!isMessagePreview(response))
    throw new Error('Unexpected message preview response');

  return response;
};

const replaceLegacyMergeTagsWithMergeTags = (content: string) => {
  return Object.entries(LegacyTagsDict).reduce(
    (accContent, [legacyMergeTag, mergeTag]) => {
      return accContent.replace(new RegExp(legacyMergeTag, 'g'), mergeTag);
    },
    content,
  );
};

const replaceLegacyMergeTagsWithSquareTags = (content: string) => {
  return content.replace(
    LEGACY_TAG_REGEX,
    (match) => `[${match.slice(1, -1).toUpperCase()}]`,
  );
};

export const replaceLegacyMergeTags = (content: string): string => {
  return replaceLegacyMergeTagsWithSquareTags(
    replaceLegacyMergeTagsWithMergeTags(content),
  );
};

/**
 * Sanitizes message content by removing invisible Unicode line/paragraph separator characters.
 * These characters (U+2028 LINE SEPARATOR and U+2029 PARAGRAPH SEPARATOR) can be accidentally
 * introduced when copy-pasting from rich text editors like Google Docs, Word, or Notion.
 * They cause rendering inconsistencies across different devices and carriers.
 *
 * @param content - The message content to sanitize
 * @returns The sanitized content with line/paragraph separators replaced with regular newlines
 */
export const sanitizeMessageContent = (content: string): string => {
  return content
    .replace(/\u2028/g, '\n') // Replace LINE SEPARATOR with newline
    .replace(/\u2029/g, '\n\n'); // Replace PARAGRAPH SEPARATOR with double newline
};

export const flowContainsRichMessageActions = (flow: Flow): boolean => {
  return flow.actions.some((action) => isRichMessageAction(action));
};

export const flowContainsSendMessageActions = (flow: DraftFlow): boolean => {
  return flow.actions.some((action) => isSendMessageAction(action));
};

/**
 * Checks if a string contains URLs using the existing URL regex from helpers.
 * Uses unstrictUrlRegex to catch both http(s):// URLs and www. URLs
 * @param text - The text to check for URLs
 * @returns true if the text contains URLs, false otherwise
 */
export const containsUrls = (text: string): boolean => {
  const { unstrictUrlRegex } = Helpers;
  unstrictUrlRegex.lastIndex = 0; // Reset regex state
  return unstrictUrlRegex.test(text);
};

/**
 * Checks if a URL is glued directly to the preceding word (i.e. is missing a
 * leading space), e.g. "Hello world!https://example.com".
 *
 * Postscript AI (Infinity Testing) can fail to generate any valid messages when
 * a URL is not separated from the surrounding text by whitespace, so we use this
 * to surface a non-blocking warning to the user.
 *
 * @param text - The text to check
 * @returns true if any URL is immediately preceded by a non-whitespace character
 */
export const hasUrlMissingLeadingSpace = (text: string): boolean => {
  if (!text) return false;
  const urlRegex = new RegExp(Helpers.unstrictUrlRegex.source, 'gi');
  let match = urlRegex.exec(text);
  while (match !== null) {
    const precedingChar = text[match.index - 1];
    if (match.index > 0 && !/\s/.test(precedingChar)) {
      return true;
    }
    match = urlRegex.exec(text);
  }
  return false;
};

export const MESSAGE_TYPES = {
  SMS: 'SMS',
  MMS: 'MMS',
  RCS_TEXT: 'RCS_TEXT',
  RCS_CARD: 'RCS_CARD',
} as const;

export type MessageType = typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES];

/**
 * Analyzes message actions to find those containing URLs.
 *
 * @param actions - Array of flow actions to analyze
 * @param typesToCheck - Optional array of message types to check. If omitted, checks all message types.
 * @returns Array of GUIDs for message actions that contain links
 */
export const analyzeMessageContentForLinks = (
  actions: Action[],
  typesToCheck?: MessageType[],
): string[] => {
  const shouldCheck = (type: MessageType) =>
    !typesToCheck || typesToCheck.length === 0 || typesToCheck.includes(type);

  const results: string[] = [];

  actions.forEach((action) => {
    const actionHasLinks = (() => {
      if (isSendMessageAction(action)) {
        const { content, mediaUrl } = action.params;
        if (!content) return false;

        const isSMS = !mediaUrl;
        const isMMS = !!mediaUrl;

        if (isSMS && shouldCheck(MESSAGE_TYPES.SMS)) {
          return containsUrls(content);
        }

        if (isMMS && shouldCheck(MESSAGE_TYPES.MMS)) {
          return containsUrls(content);
        }

        return false;
      }

      if (isRichMessageAction(action)) {
        const { content, cards } = action.params;

        if (content && shouldCheck(MESSAGE_TYPES.RCS_TEXT)) {
          return containsUrls(content);
        }

        if (cards && shouldCheck(MESSAGE_TYPES.RCS_CARD)) {
          return cards.some((card) => card.text && containsUrls(card.text));
        }

        return false;
      }

      return false;
    })();

    if (actionHasLinks) {
      results.push(action.guid);
    }
  });

  return results;
};
